import { useCallback, useMemo, useState } from "react";
import { AlertCircle, Calendar, LayoutDashboard, Receipt } from "lucide-react";
import moment from "moment";

import { usePaymentDetails } from "features/consultations/hooks/usePaymentDetails";
import { usePaymentMetrics } from "features/payments/hooks/usePaymentMetrics";
import { usePaymentsDueByPatient } from "features/payments/hooks/usePaymentsDueByPatient";

import { FILTERS_CONFIG } from "../constants";

export const usePaymentsData = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [dashboardStartDate, setDashboardStartDate] = useState(() =>
    moment().subtract(7, "days")
  );
  const [dashboardEndDate, setDashboardEndDate] = useState(() => moment());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [filters, setFilters] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [paymentsDuePage, setPaymentsDuePage] = useState(1);
  const [paymentsDueRowsPerPage, setPaymentsDueRowsPerPage] = useState(20);

  const defaultDates = useMemo(() => {
    if (activeTab === "today") {
      const today = moment().format("YYYY-MM-DD");
      return { from: today, to: today };
    }
    return {
      from: filters.startDate || null,
      to: filters.endDate || null,
    };
  }, [activeTab, filters.startDate, filters.endDate]);

  const paymentDetailsParams = useMemo(
    () => ({
      page: currentPage,
      rows: rowsPerPage,
      consultationDateFrom: defaultDates.from,
      consultationDateTo: defaultDates.to,
      paymentStatus: filters.paymentStatus || null,
      enabled: activeTab !== "dashboard",
      staleTime: 2 * 60 * 1000,
      refetchOnWindowFocus: false,
    }),
    [
      currentPage,
      rowsPerPage,
      defaultDates.from,
      defaultDates.to,
      filters.paymentStatus,
      activeTab,
    ]
  );

  const { data: paymentData, isLoading: isLoadingPayments } =
    usePaymentDetails(paymentDetailsParams);

  const allPaymentsCountParams = useMemo(
    () => ({
      page: 1,
      rows: 1,
      consultationDateFrom: null,
      consultationDateTo: null,
      paymentStatus: null,
      enabled: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }),
    []
  );

  const { data: allPaymentsData } = usePaymentDetails(allPaymentsCountParams);

  const todayCountParams = useMemo(
    () => ({
      page: 1,
      rows: 1,
      consultationDateFrom: moment().format("YYYY-MM-DD"),
      consultationDateTo: moment().format("YYYY-MM-DD"),
      paymentStatus: null,
      enabled: true,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }),
    []
  );

  const { data: todayCountData } = usePaymentDetails(todayCountParams);

  const memoizedDashboardDates = useMemo(
    () => ({ startDate: dashboardStartDate, endDate: dashboardEndDate }),
    [dashboardStartDate, dashboardEndDate]
  );

  const paymentMetrics = usePaymentMetrics({
    startDate: memoizedDashboardDates.startDate,
    endDate: memoizedDashboardDates.endDate,
    enabled: activeTab === "dashboard",
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const allPaymentsTotalsMetrics = usePaymentMetrics({
    startDate: null,
    endDate: null,
    enabled: activeTab === "all",
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const todayDates = useMemo(
    () => ({
      startDate: moment().startOf("day"),
      endDate: moment().endOf("day"),
    }),
    []
  );

  const todayPaymentsTotalsMetrics = usePaymentMetrics({
    startDate: todayDates.startDate,
    endDate: todayDates.endDate,
    enabled: activeTab === "today",
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: paymentsDueData, isLoading: isLoadingPaymentsDue } =
    usePaymentsDueByPatient({
      page: paymentsDuePage,
      rows: paymentsDueRowsPerPage,
      sortBy: "latestDueDate",
      sortDirection: "DESC",
      enabled: activeTab === "paymentsDue",
    });

  const paymentsDueCountParams = useMemo(
    () => ({
      page: 1,
      rows: 1,
      sortBy: "latestDueDate",
      sortDirection: "DESC",
      enabled: true,
    }),
    []
  );

  const { data: paymentsDueCountData } = usePaymentsDueByPatient(
    paymentsDueCountParams
  );

  const paymentCalculations = useMemo(() => {
    const paymentDetails = paymentData?.data || [];
    const totalRecords = paymentData?.totalRecords || 0;
    const allPaymentsCount = allPaymentsData?.totalRecords ?? 0;
    const todayCount = todayCountData?.totalRecords ?? 0;
    const paymentsDueCount = paymentsDueCountData?.totalRecords ?? 0;

    const filteredPayments = paymentDetails.filter((payment) => {
      if (!searchQuery) return true;
      const patientName = `${payment.patient?.firstName || ""} ${
        payment.patient?.surname || ""
      }`.toLowerCase();

      return patientName.includes(searchQuery.toLowerCase());
    });

    return {
      totalRecords,
      allPaymentsCount,
      todayCount,
      paymentsDueCount,
      filteredPayments,
    };
  }, [paymentData, allPaymentsData, todayCountData, paymentsDueCountData, searchQuery]);

  const paymentTabOptions = useMemo(
    () => [
      {
        value: "dashboard",
        label: "Dashboard",
        icon: <LayoutDashboard size={20} strokeWidth={2} />,
      },
      {
        value: "today",
        label: "Today",
        icon: <Calendar size={20} strokeWidth={2} />,
        count:
          activeTab === "today"
            ? paymentCalculations.totalRecords
            : paymentCalculations.todayCount,
      },
      {
        value: "all",
        label: "All Payments",
        icon: <Receipt size={20} strokeWidth={2} />,
        count:
          activeTab === "all"
            ? paymentCalculations.totalRecords
            : paymentCalculations.allPaymentsCount,
      },
      {
        value: "paymentsDue",
        label: "Payments Due",
        icon: <AlertCircle size={20} strokeWidth={2} />,
        count:
          activeTab === "paymentsDue"
            ? (paymentsDueData?.totalRecords ?? 0)
            : paymentCalculations.paymentsDueCount,
      },
    ],
    [
      activeTab,
      paymentCalculations.totalRecords,
      paymentCalculations.allPaymentsCount,
      paymentCalculations.todayCount,
      paymentCalculations.paymentsDueCount,
      paymentsDueData?.totalRecords,
    ]
  );

  const selection = useMemo(
    () => ({
      selectedCount: selectedIds.size,
      isSelected: (id) => selectedIds.has(id),
      toggleSelection: (id) =>
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.has(id) ? next.delete(id) : next.add(id);
          return next;
        }),
      selectAll: (rows) =>
        setSelectedIds(
          new Set(rows.map((r) => r.maxPaymentDueId ?? r.consultation?.id ?? r.consultationId))
        ),
      deselectAll: () => setSelectedIds(new Set()),
    }),
    [selectedIds]
  );

  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    setCurrentPage(1);
    setRowsPerPage(20);
    setPaymentsDuePage(1);
    setPaymentsDueRowsPerPage(20);
    setSelectedIds(new Set());
    if (newTab === "today") {
      const today = moment().format("YYYY-MM-DD");
      setFilters({ startDate: today, endDate: today });
    } else {
      setFilters({});
    }
    setSearchQuery("");
  }, []);

  const handleFilterChange = useCallback((groupId, value) => {
    setFilters((prev) => ({ ...prev, [groupId]: value }));
    setCurrentPage(1);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setSearchQuery("");
    setCurrentPage(1);
  }, []);

  return {
    activeTab,
    selection,
    dashboardStartDate,
    dashboardEndDate,
    setDashboardStartDate,
    setDashboardEndDate,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    filters,
    searchQuery,
    setSearchQuery,
    isLoadingPayments,
    paymentCalculations,
    paymentMetrics,
    allPaymentsTotalsMetrics,
    todayPaymentsTotalsMetrics,
    paymentTabOptions,
    handleTabChange,
    handleFilterChange,
    clearAllFilters,
    FILTERS_CONFIG,
    paymentsDueData: paymentsDueData?.data || [],
    paymentsDueTotalRecords: paymentsDueData?.totalRecords || 0,
    isLoadingPaymentsDue,
    paymentsDuePage,
    setPaymentsDuePage,
    paymentsDueRowsPerPage,
    setPaymentsDueRowsPerPage,
  };
};
